import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { batch } from 'react-redux'
import {
  fetchAffiliate,
  fetchAffiliateMembers,
  fetchInvitations,
} from 'services/affiliates'
import { Affiliate, AffiliateMember, Invitation } from 'types'
import { updateAll } from './DirectoryPage/basicInfo'
import { updateAll as updateAllPhotos } from './DirectoryPage/photos'
import { updateHighlightVisibility } from './DirectoryPage/highlights'
import { ToggleValue } from 'components/Admin/Shared/Toggle'
import {
  fetchAffiliateCompetitions,
  FetchAffiliateCompetitionsResponse,
} from 'services/competitions'
import { AffiliateCompetitionRecord } from 'types/Affiliate'
import basicInfoReducer from './DirectoryPage/basicInfo'
import photosReducer from './DirectoryPage/photos'
import highlightsReducer from './DirectoryPage/highlights'

export const initialState: Affiliate & {
  invitations?: Invitation[]
  members?: AffiliateMember[]
  competitions?: AffiliateCompetitionRecord[]
  basicInfo?: ReturnType<typeof basicInfoReducer>
  photos?: ReturnType<typeof photosReducer>
  highlights?: ReturnType<typeof highlightsReducer>
} = {}

export const fetchAffiliateThunk = createAsyncThunk(
  'affiliate/fetchAffiliate',
  async (affiliateId: number, { dispatch }) => {
    const affiliate = await fetchAffiliate(String(affiliateId))

    batch(() => {
      dispatch(updateAll(affiliate))
      dispatch(updateAllPhotos(affiliate))
      dispatch(
        updateHighlightVisibility({
          yearsAffiliated:
            affiliate.settings['yearsAffiliated']['isPublic'] === true
              ? ToggleValue.PUBLIC
              : ToggleValue.PRIVATE,
          coachCredentials:
            affiliate.settings['certifications']['isPublic'] === true
              ? ToggleValue.PUBLIC
              : ToggleValue.PRIVATE,
        }),
      )
      // Thunks: Async requests

      dispatch(fetchAffiliateMembersThunk(affiliateId))
      dispatch(fetchAffiliateInvitationsThunk(affiliateId))
      dispatch(fetchAffiliateCompetitionsThunk(affiliateId))
    })

    return affiliate
  },
)

export const fetchAffiliateInvitationsThunk = createAsyncThunk(
  'affiliate/fetchAffiliateInvitations',
  async (affiliateId: number) => {
    return await fetchInvitations(affiliateId)
  },
)

export const fetchAffiliateMembersThunk = createAsyncThunk(
  'affiliateMembers',
  async (affiliateId: number) => {
    return await fetchAffiliateMembers(affiliateId)
  },
)

export const fetchAffiliateCompetitionsThunk = createAsyncThunk(
  'affiliateCompetitions/fetchAffiliateCompetitions',
  async (affiliateId: number) => {
    return await fetchAffiliateCompetitions(affiliateId)
  },
)

export const affiliateSlice = createSlice({
  name: 'affiliate',
  initialState,
  reducers: {
    updateAffiliateAttribute: (state, action: PayloadAction<Affiliate>) => {
      state = { ...state, ...action.payload }
      return state
    },
    updateCoachImage: (
      state,
      action: PayloadAction<{ userId: number; imageUrl: string }>,
    ) => {
      const coachIndex = state.images?.coaches?.findIndex(
        (c) => c.id === action.payload.userId,
      )

      if (coachIndex !== -1) {
        const coaches = [...state.images.coaches]
        coaches[coachIndex] = {
          ...coaches[coachIndex],
          image: { url: action.payload.imageUrl },
        }
        state.images = {
          coaches: coaches,
        }
      }

      return state
    },
    updateAffiliateMembers: (
      state,
      action: PayloadAction<AffiliateMember[]>,
    ) => {
      state.members = action.payload

      return state
    },
    removeAffiliateMember: (
      state,
      action: PayloadAction<{ userId: number }>,
    ) => {
      state.members = state.members?.filter(
        (m) => m.id !== action.payload.userId,
      )

      return state
    },
    updateAffiliateMemberVisibility: (
      state,
      action: PayloadAction<{ userId: number; visibility: ToggleValue }>,
    ) => {
      const memberIndex = state.members.findIndex(
        (m) => m.id === action.payload.userId,
      )

      if (memberIndex !== -1) {
        state.members[memberIndex].isPublic =
          action.payload.visibility === ToggleValue.PUBLIC ? true : false
      }

      return state
    },
    addInvitations: (state, action: PayloadAction<Invitation[]>) => {
      state.invitations = [...action.payload, ...state.invitations]

      return state
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAffiliateThunk.fulfilled,
        (state, action: PayloadAction<Affiliate>) => {
          return { ...state, ...action.payload }
        },
      )
      .addCase(
        fetchAffiliateInvitationsThunk.fulfilled,
        (state, action: PayloadAction<Invitation[]>) => {
          state.invitations = action.payload
          return state
        },
      )
      .addCase(
        fetchAffiliateMembersThunk.fulfilled,
        (state, action: PayloadAction<AffiliateMember[]>) => {
          state.members = action.payload

          return state
        },
      )
      .addCase(
        fetchAffiliateCompetitionsThunk.fulfilled,
        (state, action: PayloadAction<FetchAffiliateCompetitionsResponse>) => {
          state.competitions = action.payload.competitions

          return state
        },
      )
      .addDefaultCase((state, action) => {
        state.basicInfo = basicInfoReducer(state.basicInfo, action)
        state.photos = photosReducer(state.photos, action)
        state.highlights = highlightsReducer(state.highlights, action)
      })
  },
})

export const {
  updateAffiliateAttribute,
  updateCoachImage,
  updateAffiliateMembers,
  updateAffiliateMemberVisibility,
  removeAffiliateMember,
  addInvitations,
} = affiliateSlice.actions
export default affiliateSlice.reducer
